import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, GridColumns } from "@mui/x-data-grid";
import { useApiUrl } from "@refinedev/core";
import { List, useDataGrid } from "@refinedev/mui";
import { OrcaDeployLifecycle } from "components/orcaDeployLifecycle";
import React, { useMemo, useState } from "react";
import { dataProvider } from "rest-data-provider";
import {
  fetchLatestOrcaDeployActivityLogRow,
  formatNyTimestamp,
  OrcaDeployActivityLogRow,
  OrcaDeployState,
  OrcaDeployStateLabels,
  ServerSlotKey,
  slotLabel,
} from "rest-data-provider/utils/orcaDeployActivityLogsTypes";
import {
  deploy,
  displayOldVersionLink,
  hideOldVersionLink,
  rollback,
} from "rest-data-provider/utils/orcaDeployActivityStateMachine";

const getCurrentOperatorName = (): string => {
  try {
    const userData = JSON.parse(
      window.sessionStorage.getItem("user") as string
    );
    return userData?.nickname || userData?.email || userData?.id || "";
  } catch {
    return "";
  }
};

type Action = "deploy" | "rollback" | "displayOldVersionLink" | "hideOldVersionLink";

const ACTION_LABELS: Record<Action, string> = {
  deploy: "Deploy",
  rollback: "Rollback",
  displayOldVersionLink: "Display Old Version Link",
  hideOldVersionLink: "Hide Old Version Link",
};

const ACTION_HINTS: Record<Action, string> = {
  deploy:
    "Promotes the current second (staged) server to prod. The old prod becomes second. State moves to stable — no second link shown to users.",
  rollback:
    "Something is wrong with the current prod. Swaps prod back to the previous server and reopens it for staging. State moves to stage.",
  displayOldVersionLink:
    "Shows the old prod as a fallback link to all users while investigating an issue. State moves to unstable.",
  hideOldVersionLink:
    "Closes the fallback link once prod is confirmed healthy again. State moves back to stable.",
};

const emptyRow: OrcaDeployActivityLogRow = {
  id: "",
  result: [],
  server1: null,
  server2: null,
  server3: null,
  server4: null,
  prod: "server1",
  second: "server2",
  state: OrcaDeployState.STAGE,
  prodLinkName: "",
  prodLinkDescription: "",
  fallbackLinkName: "",
  fallbackDescription: "",
  betaLinkName: "",
  betaLinkDescription: "",
  activityName: "",
  activityOperatorName: "",
  comment: null,
  createdAt: 0,
};

export const OrcaDeploymentList = () => {
  const apiUrl = useApiUrl();

  const { dataGridProps } = useDataGrid({
    resource: "orca-deploy-activity-logs",
    sorters: {
      initial: [{ field: "createdAt", order: "desc" }],
    },
    pagination: { pageSize: 10 },
  });

  // dataGridProps.rows is already sorted by createdAt desc (page 1, row 0 is
  // the latest), so derive `latest` from it instead of firing a second
  // $limit=1 request for the same row.
  const latest: OrcaDeployActivityLogRow = useMemo(
    () => (dataGridProps.rows[0] as OrcaDeployActivityLogRow) ?? emptyRow,
    [dataGridProps.rows]
  );
  const [pendingAction, setPendingAction] = useState<Action | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const columns = React.useMemo<GridColumns<any>>(
    () => [
      {
        field: "createdAt",
        headerName: "Timestamp",
        minWidth: 200,
        hideable: false,
        renderCell: ({ row }) => formatNyTimestamp(row.createdAt),
      },
      {
        field: "activityName",
        headerName: "Activity",
        minWidth: 160,
        hideable: false,
      },
      {
        field: "state",
        headerName: "State",
        minWidth: 130,
        hideable: false,
        renderCell: ({ row }) => OrcaDeployStateLabels[row.state as 0 | 1 | 2],
      },
      {
        field: "activityOperatorName",
        headerName: "Operator",
        minWidth: 150,
        hideable: false,
      },
      {
        field: "prod",
        headerName: "Prod Domain",
        minWidth: 180,
        flex: 1,
        hideable: false,
        renderCell: ({ row }) => slotLabel(row, row.prod),
      },
      {
        field: "prodVersion",
        headerName: "Prod Version",
        minWidth: 120,
        renderCell: ({ row }) => row[row.prod as ServerSlotKey]?.version || "—",
      },
      {
        field: "prodHash",
        headerName: "Prod Hash",
        minWidth: 120,
        renderCell: ({ row }) => row[row.prod as ServerSlotKey]?.hash || "—",
      },
      {
        field: "second",
        headerName: "Second Domain",
        minWidth: 180,
        flex: 1,
        hideable: false,
        renderCell: ({ row }) => slotLabel(row, row.second),
      },
      {
        field: "secondVersion",
        headerName: "Second Version",
        minWidth: 130,
        renderCell: ({ row }) => row[row.second as ServerSlotKey]?.version || "—",
      },
      {
        field: "secondHash",
        headerName: "Second Hash",
        minWidth: 120,
        renderCell: ({ row }) => row[row.second as ServerSlotKey]?.hash || "—",
      },
      // Last column, and the only free-form one: a comment can be far longer
      // than any cell width, so the cell truncates and the full text (with
      // its line breaks preserved) is shown on hover.
      {
        field: "comment",
        headerName: "Comment",
        minWidth: 220,
        flex: 1,
        renderCell: ({ row }) =>
          row.comment ? (
            <Tooltip
              title={<Box sx={{ whiteSpace: "pre-wrap" }}>{row.comment}</Box>}
              placement="top"
            >
              <Box
                sx={{
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.comment}
              </Box>
            </Tooltip>
          ) : (
            "—"
          ),
      },
    ],
    []
  );

  const nextRowFor = (
    action: Action,
    base: OrcaDeployActivityLogRow
  ): OrcaDeployActivityLogRow => {
    const derived =
      action === "deploy"
        ? deploy(base)
        : action === "rollback"
        ? rollback(base)
        : action === "displayOldVersionLink"
        ? displayOldVersionLink(base)
        : hideOldVersionLink(base);
    return { ...base, ...derived };
  };

  const actionsAvailableFor = (row: OrcaDeployActivityLogRow): Action[] => {
    const secondPresent = !!row[row.second];
    if (row.state === OrcaDeployState.STAGE) {
      return secondPresent ? ["deploy"] : [];
    }
    if (row.state === OrcaDeployState.STABLE) {
      return secondPresent ? ["rollback", "displayOldVersionLink"] : [];
    }
    if (row.state === OrcaDeployState.UNSTABLE) {
      return secondPresent ? ["rollback", "hideOldVersionLink"] : [];
    }
    return [];
  };

  // deploy/rollback/displayOldVersionLink all promote or surface `second` —
  // without a second slot they'd swap in/publish an empty-url entry.
  // hideOldVersionLink only narrows result, so it's fine with no second.
  const hasSecondSlot = !!latest[latest.second];
  const availableActions = actionsAvailableFor(latest);

  const handleConfirm = async () => {
    if (!pendingAction) return;
    setSubmitting(true);
    try {
      // Re-fetch right before writing: another operator may have appended
      // a row since this page loaded. Build the new row on top of whatever
      // is actually latest now, not the possibly-stale `latest` from state.
      const freshLatest =
        (await fetchLatestOrcaDeployActivityLogRow(apiUrl)) ?? latest;
      if (!actionsAvailableFor(freshLatest).includes(pendingAction)) {
        window.alert(
          "Someone else changed the deploy state while this dialog was open. " +
            "Please review the latest state and try again."
        );
        setPendingAction(null);
        window.location.reload();
        return;
      }
      const next = nextRowFor(pendingAction, freshLatest);
      await dataProvider(apiUrl).create({
        resource: "orca-deploy-activity-logs",
        variables: {
          ...next,
          id: undefined,
          activityName: ACTION_LABELS[pendingAction],
          activityOperatorName: getCurrentOperatorName(),
          // `next` is built on top of freshLatest, which carries that row's
          // comment — blank it so a note written on an earlier activity
          // doesn't get re-attached to this one.
          comment: null,
          createdAt: Date.now(),
        },
      });
      window.location.reload();
    } catch (error: any) {
      window.alert(error?.message || "Failed to record deploy activity");
      setPendingAction(null);
    } finally {
      setSubmitting(false);
    }
  };

  const next = pendingAction ? nextRowFor(pendingAction, latest) : null;

  // Deploy/Rollback swap the prod/second pointers, so Prod/Second (and their
  // hash/version) are worth comparing before/after. Display/Hide Old
  // Version Link only ever change `state` — pointers never move — so
  // showing Prod/Second rows for those would just repeat the same value
  // twice with a no-op arrow.
  const showsPointerChanges =
    pendingAction === "deploy" || pendingAction === "rollback";

  return (
    <List title="Orca Deployment">
      <OrcaDeployLifecycle state={latest.state} />

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Current Prod
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                {slotLabel(latest, latest.prod)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Current Second
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                {slotLabel(latest, latest.second)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                State
              </Typography>
              <Box>
                <Chip
                  label={OrcaDeployStateLabels[latest.state]}
                  color={
                    latest.state === OrcaDeployState.STABLE
                      ? "success"
                      : latest.state === OrcaDeployState.UNSTABLE
                      ? "warning"
                      : "default"
                  }
                  size="small"
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {availableActions.map((action) => (
              <Button
                key={action}
                variant="contained"
                color={action === "rollback" ? "error" : "primary"}
                onClick={() => setPendingAction(action)}
              >
                {ACTION_LABELS[action]}
              </Button>
            ))}
            {availableActions.length === 0 && (
              <Typography color="text.secondary">
                {!hasSecondSlot && latest.state !== OrcaDeployState.UNSTABLE
                  ? "No Second slot is set — use the Developer page's Create New to set one."
                  : "No actions available for the current state."}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        Operation Log
      </Typography>
      <DataGrid
        getRowId={(e) => e.id}
        {...dataGridProps}
        columns={columns}
        autoHeight
        paginationMode="server"
        rowsPerPageOptions={[10, 20, 50]}
        disableColumnFilter
      />

      <Dialog
        open={!!pendingAction}
        onClose={() => setPendingAction(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Confirm: {pendingAction ? ACTION_LABELS[pendingAction] : ""}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {pendingAction ? ACTION_HINTS[pendingAction] : ""}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell>Current</TableCell>
                <TableCell align="center" />
                <TableCell>New</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>State</TableCell>
                <TableCell sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                  {OrcaDeployStateLabels[latest.state]}
                </TableCell>
                <TableCell align="center" sx={{ color: "text.secondary" }}>
                  →
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontWeight: 600, color: "warning.main" }}>
                  {next ? OrcaDeployStateLabels[next.state] : ""}
                </TableCell>
              </TableRow>
              {showsPointerChanges && (
                <>
                  <TableRow>
                    <TableCell>Prod</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                      {slotLabel(latest, latest.prod)}
                    </TableCell>
                    <TableCell align="center" sx={{ color: "text.secondary" }}>
                      →
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "monospace",
                        fontWeight: next && next.prod !== latest.prod ? 600 : 400,
                        color: next && next.prod !== latest.prod ? "warning.main" : "text.primary",
                      }}
                    >
                      {next ? slotLabel(next, next.prod) : ""}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Prod Version</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                      {latest[latest.prod]?.version || "—"}
                    </TableCell>
                    <TableCell align="center" sx={{ color: "text.secondary" }}>
                      →
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {(next && next[next.prod]?.version) || "—"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Prod Hash</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                      {latest[latest.prod]?.hash || "—"}
                    </TableCell>
                    <TableCell align="center" sx={{ color: "text.secondary" }}>
                      →
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {(next && next[next.prod]?.hash) || "—"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Second</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                      {slotLabel(latest, latest.second)}
                    </TableCell>
                    <TableCell align="center" sx={{ color: "text.secondary" }}>
                      →
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "monospace",
                        fontWeight: next && next.second !== latest.second ? 600 : 400,
                        color: next && next.second !== latest.second ? "warning.main" : "text.primary",
                      }}
                    >
                      {next ? slotLabel(next, next.second) : ""}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Second Version</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                      {latest[latest.second]?.version || "—"}
                    </TableCell>
                    <TableCell align="center" sx={{ color: "text.secondary" }}>
                      →
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {(next && next[next.second]?.version) || "—"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Second Hash</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                      {latest[latest.second]?.hash || "—"}
                    </TableCell>
                    <TableCell align="center" sx={{ color: "text.secondary" }}>
                      →
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {(next && next[next.second]?.hash) || "—"}
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
          <Typography
            variant="body2"
            sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: "warning.light", color: "warning.contrastText" }}
          >
            This is an append — it will not overwrite or delete any historical
            record.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingAction(null)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} variant="contained" disabled={submitting}>
            Confirm &amp; Add
          </Button>
        </DialogActions>
      </Dialog>
    </List>
  );
};
